import {inject, Injectable} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {TaskControllerService, WorkflowDefinitionControllerService} from '../../api';
import {RulesetService} from '../ruleset.service';

/** Die Arten von Objekten, auf denen der Nutzer stehen kann. */
export type ChatContextEntityKind = 'mycheck' | 'checkrun' | 'run' | 'report' | 'task';

/**
 * Löst die Kennung aus der Route in einen lesbaren Namen auf.
 *
 * <p>Damit zeigt der Chat nicht nur „myChecks" als Modul, sondern <em>welchen</em> myCheck man
 * gerade offen hat – und damit auch, worauf sich der Edit-Modus bezieht.
 *
 * <p><b>Nie die Kennung selbst anzeigen.</b> Lässt sich kein Name ermitteln, bleibt das Feld leer
 * und die Oberfläche zeigt gar kein Objekt-Badge. Interne Kennungen im Text sind genau das, was der
 * Assistent auf der anderen Seite herausfiltert; sie über die Oberfläche wieder hereinzulassen wäre
 * widersinnig.
 *
 * <p>Aufgelöste Namen werden gemerkt: beim Blättern zwischen Regelwerken wird sonst jede Navigation
 * zu einem Serveraufruf.
 */
@Injectable({providedIn: 'root'})
export class ChatContextLabelService {
  private rulesetService = inject(RulesetService);
  private taskController = inject(TaskControllerService);
  private definitionController = inject(WorkflowDefinitionControllerService);

  /** `<kind>:<id>` → Name. */
  private readonly cache = new Map<string, string>();
  /** Laufende Auflösungen, damit parallele Navigationen nicht mehrfach laden. */
  private readonly inFlight = new Map<string, Promise<string | undefined>>();

  async resolve(kind: ChatContextEntityKind, id: string, projectId?: number): Promise<string | undefined> {
    if (!id) return undefined;

    const key = `${kind}:${id}`;
    const cached = this.cache.get(key);
    if (cached) return cached;

    const running = this.inFlight.get(key);
    if (running) return running;

    const lookup = this.lookup(kind, id, projectId)
      .then(name => {
        if (name) this.cache.set(key, name);
        return name;
      })
      .catch(() => undefined)
      .finally(() => this.inFlight.delete(key));

    this.inFlight.set(key, lookup);
    return lookup;
  }

  private async lookup(kind: ChatContextEntityKind, id: string, projectId?: number): Promise<string | undefined> {
    switch (kind) {
      case 'task': {
        const task = await firstValueFrom(this.taskController.getTask(id));
        return task?.title?.trim() || undefined;
      }
      case 'checkrun': {
        if (projectId == null) return undefined;
        const definition = await firstValueFrom(this.definitionController.getDefinition(id, projectId));
        return definition?.name?.trim() || undefined;
      }
      case 'run':
      case 'report':
        // Kein günstiger Einzelabruf: die Instanz-Details serialisieren alle StepLogs (mehrere MB
        // bei großen Läufen). Den Berichtsnamen liefert die Route selbst (`checkName`), wo es ihn
        // gibt – sonst bleibt das Badge leer.
        return undefined;
      case 'mycheck': {
        // Es gibt keinen Einzelabruf für ein Regelwerk: die drei Arten liegen in verschiedenen
        // Ablagen und werden nur als Liste zusammengeführt. Der Aufruf lohnt trotzdem – danach
        // sind alle Namen des Projekts gemerkt.
        const {prebuilt, custom} = await firstValueFrom(this.rulesetService.listRulesets(projectId));
        for (const ruleset of [...custom, ...prebuilt]) {
          if (ruleset.id) this.cache.set(`mycheck:${ruleset.id}`, ruleset.name);
        }
        return this.cache.get(`mycheck:${id}`);
      }
    }
  }

  /** Nach dem Umbenennen oder Löschen eines Objekts. */
  forget(kind: ChatContextEntityKind, id: string): void {
    this.cache.delete(`${kind}:${id}`);
  }
}
